import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { GroupListItemDataFragment, SubgroupListItemDataFragment } from '../GroupListing/GroupList.api';
import type { SubgroupFormData } from '../shared/SubgroupFormConfig';
import SubgroupFormContainer from '../shared/SubgroupFormContainer';
import type { UpdateSubgroupMutation, UpdateSubgroupMutationVariables } from './UpdateSubgroup.api';
import { UpdateSubgroupDocument } from './UpdateSubgroup.api';
import client from '@/apolloClient';
import '@/utils/notifications';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';

interface UpdateSubgroupProps {
    open: boolean;
    companyId: string;
    parentGroup: GroupListItemDataFragment | null;
    subgroup: SubgroupListItemDataFragment | null;
    onClose: () => void;
    onCreated: () => void;
}

const EditSubgroup = ({ open, onClose, onCreated, companyId, parentGroup, subgroup }: UpdateSubgroupProps) => {
    const { t } = useTranslation('groupDetails');

    const handleSubmit = useCallback(
        async (formData: SubgroupFormData) => {
            try {
                const fullFormData = {
                    ...formData,
                    parentGroupId: parentGroup?.id || '',
                    companyId: companyId,
                };
                // Omit parentGroupName as it is used only for display and not required by the mutation input
                const { parentGroupName, ...inputData } = fullFormData;
                const variables: UpdateSubgroupMutationVariables = {
                    id: subgroup?.id || '',
                    input: inputData,
                };

                const { data } = await client.mutate<UpdateSubgroupMutation>({
                    mutation: UpdateSubgroupDocument,
                    variables,
                });

                if (data?.updateSubgroup?.__typename === 'UpdateSubgroupSuccessfulResponse') {
                    if (onCreated) {
                        onCreated();
                    } else {
                        onClose();
                    }
                    showSuccessNotification({
                        message: t('notifications.updateSubgroupTitle'),
                        description: t('notifications.updateSubgroupDescription', { name: formData.name }),
                    });
                    return;
                }

                if (data?.updateSubgroup?.__typename === 'ErrorResponse') {
                    const error = {
                        fields: data.updateSubgroup.fields,
                    };
                    showErrorNotification({
                        message: t('notifications.updateErrorTitle'),
                        description: error.fields?.[0]?.message || t('notifications.updateErrorDescription'),
                    });
                    throw error;
                }

                throw new Error('Unknown error creating subgroup');
            } catch (error) {
                console.error('Error creating subgroup:', error);
                // Handle error appropriately
            }
        },
        [t, onCreated, onClose, companyId, parentGroup, subgroup]
    );

    return (
        <SubgroupFormContainer
            mode="edit"
            companyId={companyId}
            subgroup={subgroup}
            group={parentGroup}
            open={open}
            onClose={onClose}
            onSubmit={handleSubmit}
            onOpenChange={onClose}
            title={t('modalEditSubgroup.title')}
        />
    );
};

export default EditSubgroup;
